import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useScenes } from "@/lib/scenes";
import { formatRelative } from "@/lib/format-time";
import { useTheme } from "@/lib/theme";
import NewSceneModal from "@/components/admin/NewSceneModal";

type SceneMeta = {
  sceneId: string;
  itemCount: number;
  hasDraft: boolean;
  lastUpdated: string | null;
};

export default function AdminDashboardPage() {
  const { theme } = useTheme();
  const onAccent = theme === "light" ? "#000" : "#fff";
  const scenesFromHook = useScenes();
  const sceneList = useMemo(() => scenesFromHook ?? [], [scenesFromHook]);
  const [meta, setMeta] = useState<Record<string, SceneMeta>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showNewScene, setShowNewScene] = useState(false);

  const existingSlugs = useMemo(() => new Set(sceneList.map((s) => s.slug)), [sceneList]);

  const fetchMeta = () => {
    fetch("/api/admin/scene-data/scene-meta", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { scenes: [] }))
      .then((data: { scenes: SceneMeta[] }) => {
        const map: Record<string, SceneMeta> = {};
        for (const s of data.scenes) map[s.sceneId] = s;
        setMeta(map);
      })
      .catch(() => {});
  };
  useEffect(() => {
    fetchMeta();
  }, []);

  const draftScenes = useMemo(
    () => Object.values(meta).filter((s) => s.hasDraft).map((s) => s.sceneId),
    [meta]
  );
  const draftCount = draftScenes.length;

  const recentlyEdited = useMemo(() => {
    return [...Object.values(meta)]
      .filter((s) => s.lastUpdated)
      .sort((a, b) => (b.lastUpdated! < a.lastUpdated! ? -1 : 1))
      .slice(0, 4);
  }, [meta]);

  const toggleSelected = (sceneId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  };

  const selectAllDrafts = () => setSelected(new Set(draftScenes));
  const clearSelection = () => setSelected(new Set());

  const publishSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Publish ${selected.size} draft${selected.size === 1 ? "" : "s"} to live?`)) return;
    setPublishing(true);
    try {
      const res = await fetch("/api/admin/scene-data/publish-bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ sceneIds: Array.from(selected) }),
      });
      if (!res.ok) {
        setToast("Bulk publish failed");
      } else {
        const data = (await res.json()) as { results: { sceneId: string; ok: boolean }[] };
        const ok = data.results.filter((r) => r.ok).length;
        const fail = data.results.length - ok;
        setToast(fail === 0 ? `Published ${ok} scenes` : `${ok} published, ${fail} failed`);
        setSelected(new Set());
        fetchMeta();
      }
      setTimeout(() => setToast(null), 5000);
    } catch {
      setToast("Network error");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <main className="landing" style={{ paddingTop: 16 }}>
      {recentlyEdited.length > 0 && (
        <section className="landing-panel" style={{ borderRadius: 0 }}>
          <div className="landing-header">
            <div>
              <p className="section-label">Recently edited</p>
              <h2>Pick up where you left off</h2>
            </div>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 12,
          }}>
            {recentlyEdited.map((m) => {
              const scene = sceneList.find((s) => s.slug === m.sceneId);
              return (
                <Link
                  key={m.sceneId}
                  to={`/admin/scene-builder/${m.sceneId}`}
                  className="scene-choice-card"
                  style={{ position: "relative", padding: 8 }}
                >
                  {m.hasDraft && (
                    <span style={{
                      position: "absolute", top: 6, right: 6, zIndex: 1,
                      fontSize: "0.66rem", fontWeight: 700, padding: "2px 6px",
                      borderRadius: 999, background: "#f58231", color: "#fff",
                    }}>● DRAFT</span>
                  )}
                  {scene && (
                    <img
                      src={scene.image}
                      alt={scene.alt}
                      loading="lazy"
                      style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 4 }}
                    />
                  )}
                  <div style={{ padding: "6px 4px 0" }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: "0.88rem" }}>
                      {scene?.label ?? m.sceneId}
                    </p>
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                      {formatRelative(m.lastUpdated)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section style={{ padding: "16px 24px 48px", background: "transparent" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>All scenes</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {draftCount > 0 && (
              selected.size > 0 ? (
                <>
                  <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                    {selected.size} selected
                  </span>
                  <button type="button" className="secondary-button"
                    onClick={clearSelection}
                    style={{ padding: "6px 14px", fontSize: "0.85rem" }}>
                    Unselect all
                  </button>
                  <button type="button" className="primary-button"
                    onClick={publishSelected}
                    disabled={publishing}
                    style={{ padding: "6px 14px", fontSize: "0.85rem", background: "var(--accent)", color: onAccent }}>
                    {publishing ? "Publishing..." : `Publish ${selected.size}`}
                  </button>
                </>
              ) : (
                <button type="button" className="secondary-button"
                  onClick={selectAllDrafts}
                  style={{ padding: "6px 14px", fontSize: "0.85rem" }}>
                  Select all drafts
                </button>
              )
            )}
            <button type="button" className="primary-button"
              onClick={() => setShowNewScene(true)}
              style={{ padding: "6px 14px", fontSize: "0.85rem", background: "var(--accent)", color: onAccent }}>
              + New scene
            </button>
          </div>
        </div>
        <div className="scene-grid">
          {sceneList.map((scene) => {
            const m = meta[scene.slug];
            const isDraft = m?.hasDraft ?? false;
            const lastUpdated = m?.lastUpdated ?? null;
            const isSelected = selected.has(scene.slug);
            return (
              <div key={scene.slug} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
                {isDraft && (
                  <label
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute", top: 8, left: 8, zIndex: 2,
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "4px 6px", borderRadius: 6,
                      background: "rgba(255,255,255,0.92)", cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(scene.slug)}
                      style={{ margin: 0 }}
                    />
                  </label>
                )}
                <Link
                  to={`/admin/scene-builder/${scene.slug}`}
                  className="scene-choice-card"
                  style={{ position: "relative", flex: 1 }}
                >
                  {isDraft && (
                    <span
                      title="Unpublished draft"
                      style={{
                        position: "absolute", top: 8, right: 8, zIndex: 1,
                        fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em",
                        padding: "3px 8px", borderRadius: 999,
                        background: "#f58231", color: "#fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                      }}
                    >
                      ● DRAFT
                    </span>
                  )}
                  <img
                    className="scene-choice-image"
                    src={scene.image}
                    alt={scene.alt}
                    loading="lazy"
                    decoding="async"
                    style={{ aspectRatio: "4 / 3", objectFit: "cover" }}
                  />
                  <div className="scene-choice-copy">
                    <p className="section-label">{scene.label}</p>
                    <h2>{scene.title}</h2>
                    <p style={{ margin: 0 }}>{scene.itemCount} items</p>
                    {lastUpdated && (
                      <p style={{ margin: "2px 0 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                        edited {formatRelative(lastUpdated)}
                      </p>
                    )}
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {showNewScene && (
        <NewSceneModal
          existingSlugs={existingSlugs}
          onClose={() => {
            setShowNewScene(false);
            // A new scene might have been created; refetch the per-scene meta
            // so item counts + lastUpdated are accurate on the dashboard.
            fetchMeta();
          }}
        />
      )}

      {toast && (
        <div role="status" style={{
          position: "fixed", bottom: 16, right: 16, zIndex: 50,
          padding: "10px 16px", borderRadius: 8,
          background: toast.includes("failed") || toast.includes("error") ? "var(--error, #e53e3e)" : "var(--accent)",
          color: toast.includes("failed") || toast.includes("error") ? "#fff" : onAccent,
          fontSize: "0.9rem", fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        }}>
          {toast}
        </div>
      )}
    </main>
  );
}
